import { useState } from "react";
import { Dialog, Text, useToast } from "@flat-ui";
import { AdmPlan } from "@custom-types/base-types";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useAppDispatch } from "@store/store-helper";
import { deletePlan } from "@store/plans/plans-thunks";
import { formatPlanName } from "@utils/data-display";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { isDeletionConfirmed } from "@utils/plan-actions-utils";
import { DeletePlanConfirmation } from "@components/plans-table/delete-plan-confirmation";
import { PlanEvents } from "@utils/track-event/track-event-list";

interface Props {
  /** The plan that is about to get deleted */
  plan: AdmPlan;

  /** Function called when the user closes the dialog */
  onClose(): void;
}

/** Dialog to confirm that a plan is permanently deleted, removing its usage from the list */
export function DeletePlanDialog({ plan, onClose }: Props): React.JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorHandler();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [confirmationText, setConfirmationText] = useState<string>("");

  const planName = formatPlanName(plan);

  async function handleConfirm(): Promise<void> {
    trackEvent({
      name: PlanEvents.deletePlan,
      props: {
        target: plan.subject.type,
        planId: plan.id,
        planName,
      },
    });

    try {
      setIsDeleting(true);

      await dispatch(deletePlan({ planId: plan.id })).unwrap();

      openToast({
        title: "The selected plan is deleted successfully!",
      });
      onClose();
    } catch (error) {
      handleErrorWithToast({
        id: `deleting-plan-${Date.now().toString()}`,
        title: "Could not delete plan",
        error,
      });
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      title={`Delete plan "${planName}"`}
      onConfirm={handleConfirm}
      showSpinner={isDeleting}
      confirmText="Yes, delete"
      variant="danger"
      isConfirmDisabled={!isDeletionConfirmed(confirmationText)}
      confirmTooltip="Type delete to confirm"
    >
      <Text variant="bodyL">
        Deleting this plan will remove the usage from the list. This cannot be
        undone.
      </Text>
      <DeletePlanConfirmation
        confirmationText={confirmationText}
        onConfirmationTextChange={setConfirmationText}
      />
    </Dialog>
  );
}
